import { useCallback, useEffect, useState } from 'react'
import { Info, Loader2 } from 'lucide-react'
import { useNotification } from '../../../contexts/NotificationContext'
import {
  fetchCampaignFailures,
  fetchCampaignResults,
  fetchCampaigns,
} from '../../../services/crmService'
import { Badge } from '../../ui/badge'
import { Button } from '../../ui/button'

const STATUS_VARIANT = { draft: 'muted', sending: 'blue', sent: 'teal', failed: 'orange' }

function Metric({ label, value, of, caveat }) {
  const pct = of > 0 && value !== null ? Math.round((value / of) * 100) : null
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <div className="flex items-center gap-1 text-xs uppercase tracking-wide text-muted-foreground">
        {label}
        {caveat && (
          <span title={caveat} className="cursor-help">
            <Info className="h-3 w-3" />
          </span>
        )}
      </div>
      <div className="mt-1 text-2xl font-semibold">{value?.toLocaleString() ?? '—'}</div>
      {pct !== null && <div className="text-xs text-muted-foreground">{pct}% of sent</div>}
    </div>
  )
}

export default function ResultsView({ campaignId, onSelectCampaign }) {
  const notify = useNotification()
  const [campaigns, setCampaigns] = useState([])
  const [selected, setSelected] = useState(campaignId || null)
  const [results, setResults] = useState(null)
  const [failures, setFailures] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchCampaigns()
      .then((list) => {
        // Drafts have nothing to report on.
        const sent = list.filter((c) => c.status !== 'draft')
        setCampaigns(sent)
        if (!campaignId && sent.length) setSelected((current) => current ?? sent[0].id)
      })
      .catch((err) => notify.error(err.message || 'Could not load campaigns'))
  }, [campaignId, notify])

  const load = useCallback(async () => {
    if (!selected) return
    setLoading(true)
    try {
      const [summary, failed] = await Promise.all([
        fetchCampaignResults(selected),
        fetchCampaignFailures(selected),
      ])
      setResults(summary)
      setFailures(failed)
    } catch (err) {
      notify.error(err.message || 'Could not load results')
    } finally {
      setLoading(false)
    }
  }, [selected, notify])

  useEffect(() => { load() }, [load])

  const campaign = campaigns.find((c) => c.id === selected)

  if (!campaigns.length) {
    return (
      <p className="rounded-lg border border-dashed border-border py-12 text-center text-sm text-muted-foreground">
        Nothing sent yet. Results appear here once a campaign goes out.
      </p>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={selected ?? ''}
          onChange={(e) => { setSelected(e.target.value); onSelectCampaign?.(e.target.value, 'results') }}
          aria-label="Choose a campaign"
          className="h-10 min-w-[260px] rounded-md border border-input bg-background px-3 text-sm"
        >
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        {campaign && <Badge variant={STATUS_VARIANT[campaign.status] ?? 'muted'}>{campaign.status}</Badge>}
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Refresh
        </Button>
      </div>

      {campaign && (
        <p className="text-sm text-muted-foreground">
          {campaign.subject} · {campaign.sent_count.toLocaleString()} sent
          {campaign.failed_count > 0 && ` · ${campaign.failed_count.toLocaleString()} failed`}
          {campaign.sent_at && ` · ${new Date(campaign.sent_at).toLocaleString()}`}
        </p>
      )}

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-6">
        <Metric label="Sent" value={campaign?.sent_count ?? null} of={0} />
        <Metric label="Delivered" value={results?.delivered ?? null} of={campaign?.sent_count ?? 0} />
        <Metric
          label="Opened"
          value={results?.opened ?? null}
          of={campaign?.sent_count ?? 0}
          caveat="Apple Mail Privacy Protection loads open pixels without anyone reading the email, so this number is inflated. Clicks are the honest measure."
        />
        <Metric label="Clicked" value={results?.clicked ?? null} of={campaign?.sent_count ?? 0} />
        <Metric label="Bounced" value={results?.bounced ?? null} of={campaign?.sent_count ?? 0} />
        <Metric label="Unsubscribed" value={results?.unsubscribed ?? null} of={campaign?.sent_count ?? 0} />
      </div>

      <p className="flex gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Opens are counted once per recipient and skew high — Apple Mail fires
        the tracking pixel whether or not anyone read the message. Clicks are
        the number worth reporting.
        {results?.complained > 0 && (
          <strong className="text-[var(--orange-11)]">
            {' '}{results.complained} spam complaint{results.complained === 1 ? '' : 's'} — all
            auto-suppressed.
          </strong>
        )}
      </p>

      {failures.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Not delivered</h3>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-medium">Email</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Reason</th>
                </tr>
              </thead>
              <tbody>
                {failures.map((row) => (
                  <tr key={row.email} className="border-t border-border">
                    <td className="px-4 py-2">{row.email}</td>
                    <td className="px-4 py-2">
                      <Badge variant={row.status === 'failed' ? 'orange' : 'muted'}>{row.status}</Badge>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{row.error || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
